import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const RegisterPage = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  const [empresa, setEmpresa] = useState({
    nit: "",
    razon_social: "",
    correo: "",
    telefono: "",
    direccion: "",
  });

  const [usuario, setUsuario] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const websiteRef = useRef(null); // honeypot: campo oculto, un bot lo llena
  const turnstileRef = useRef(null);

  // Carga el widget de Cloudflare Turnstile bajo demanda
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    const renderWidget = () => {
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setCaptchaToken(token),
        });
      }
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = renderWidget;
    document.body.appendChild(script);
  }, []);

  const handleEmpresa = (e) =>
    setEmpresa({ ...empresa, [e.target.name]: e.target.value });
  const handleUsuario = (e) =>
    setUsuario({ ...usuario, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (TURNSTILE_SITE_KEY && !captchaToken) {
      toast.error("❌ Completa la verificación de seguridad");
      return;
    }

    if (!policyAccepted) {
      toast.error(
        "❌ Debes aceptar la Política de Privacidad y la Autorización de Tratamiento de Datos",
      );
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/register`, {
        empresa,
        usuario,
        captchaToken,
        policyAccepted,
        hp_confirm_x1: websiteRef.current?.value || "",
      });

      toast.success(
        "✅ Registro exitoso. Revisa tu correo para confirmar la cuenta.",
      );

      // Limpiar formulario opcional
      setEmpresa({
        nit: "",
        razon_social: "",
        correo: "",
        telefono: "",
        direccion: "",
      });
      setUsuario({ name: "", email: "", password: "" });

      setTimeout(() => {
        navigate("/login");
      }, 2500);
    } catch (err) {
      // Mostrar mensaje de error del backend
      const mensajeError =
        err.response?.data?.message || "Error al registrarse";
      toast.error(`❌ ${mensajeError}`);
      if (window.turnstile) {
        window.turnstile.reset();
        setCaptchaToken("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative bg-gray-900 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center filter brightness-50"
        style={{ backgroundImage: "url('/login-bg.jpg')" }}
      />

      <div className="relative z-10 w-full max-w-md mx-4 p-10 bg-white/90 rounded-3xl shadow-2xl backdrop-blur-md animate-fadeIn">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
          Crear una cuenta
        </h2>
        <p className="text-center text-gray-600 mb-8">
          Registra tu empresa y tu usuario administrador
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot anti-bot: oculto para humanos, los bots suelen rellenarlo */}
          <input
            type="text"
            name="hp_confirm_x1"
            id="hp_confirm_x1"
            ref={websiteRef}
            tabIndex={-1}
            autoComplete="off"
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }}
            aria-hidden="true"
          />

          <h3 className="font-semibold text-gray-700">
            Información de la empresa
          </h3>
          <input
            type="text"
            name="nit"
            placeholder="NIT"
            className="w-full border p-3 rounded-xl"
            onChange={handleEmpresa}
            required
          />
          <input
            type="text"
            name="razon_social"
            placeholder="Razón Social"
            className="w-full border p-3 rounded-xl"
            onChange={handleEmpresa}
            required
          />
          <input
            type="email"
            name="correo"
            placeholder="Correo empresarial"
            className="w-full border p-3 rounded-xl"
            onChange={handleEmpresa}
            required
          />
          <input
            type="text"
            name="telefono"
            placeholder="Teléfono"
            className="w-full border p-3 rounded-xl"
            onChange={handleEmpresa}
          />
          <input
            type="text"
            name="direccion"
            placeholder="Dirección"
            className="w-full border p-3 rounded-xl"
            onChange={handleEmpresa}
          />

          <h3 className="font-semibold text-gray-700 mt-6">
            Usuario administrador
          </h3>
          <input
            type="text"
            name="name"
            placeholder="Nombre completo"
            className="w-full border p-3 rounded-xl"
            onChange={handleUsuario}
            required
          />
          <input
            type="email"
            name="email"
            placeholder="Correo del usuario"
            className="w-full border p-3 rounded-xl"
            onChange={handleUsuario}
            required
          />
          <input
            type="password"
            name="password"
            placeholder="Contraseña"
            className="w-full border p-3 rounded-xl"
            onChange={handleUsuario}
            required
          />

          {TURNSTILE_SITE_KEY && (
            <div ref={turnstileRef} className="flex justify-center" />
          )}

          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={policyAccepted}
              onChange={(e) => setPolicyAccepted(e.target.checked)}
              className="mt-1"
              required
            />
            <span>
              He leído y acepto la{" "}
              <a
                href="/politica-privacidad"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                Política de Privacidad
              </a>{" "}
              y la{" "}
              <a
                href="/tratamiento-datos"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                Autorización de Tratamiento de Datos
              </a>
              .
            </span>
          </label>

          <Button
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            disabled={loading || !policyAccepted}
          >
            {loading ? "Registrando..." : "Crear cuenta"}
          </Button>
        </form>

        <div className="text-center mt-6">
          <button
            className="text-blue-600 font-semibold hover:text-blue-800"
            onClick={() => navigate("/login")}
          >
            ¿Ya tienes cuenta? Inicia sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
